import { IAttachmentFile } from '@honeybook/hbui/dist/components/HBFileThumbnail/HBFilesThumbnails';
import last from 'lodash/last';
import sortBy from 'lodash/sortBy';
import { IImagePreview } from '~/common/MultipleUploadPane/MultipleUploadingState';
import { TUploadedFile } from '~/common/useAssetLibrary';
import { trackError } from '~/services/logger/error.utils';
import { IWorkspaceAttachments } from '~/types/model.types';
import {
    FileDetails,
    ICompanyTypeEnum,
    IS3UploadFileResponse,
    IUpdateAssetsResponse,
    TCompanyTypes
} from '~/types/server.types';
import { arraySorter } from '~/utils/sort.utils';

export const getCompanyTypes = (companyTypeEnum, isMSPEligable) => {
    const extraTypes = [
        {
            key: 'event_planning',
            text: 'COMPANY_SETTING.LABELS._TYPE_EVENT_PLANNING_',
            parentType: 'planning'
        }
    ];
    const relevantCopmanyTypeEnum = isMSPEligable
        ? companyTypeEnum.filter(companyType => !companyType.old_msp)
        : companyTypeEnum.filter(companyType => !companyType.new_msp);

    const typesWithoutOther = relevantCopmanyTypeEnum.slice(
        0,
        relevantCopmanyTypeEnum.length - 1
    );
    const allTypes = [...typesWithoutOther, ...extraTypes];

    const companyTypeEnumOther = last(
        relevantCopmanyTypeEnum
    ) as ICompanyTypeEnum;

    let _companyTypes = allTypes.map(companyType => {
        return {
            label: companyType.text,
            value: companyType.key,
            parentType: companyType.parentType
        };
    });

    const translatedCompanyTypes = translateCompanyTypes(_companyTypes);

    _companyTypes = sortBy(translatedCompanyTypes, 'label');

    _companyTypes.push({
        label: 'Other',
        value: companyTypeEnumOther.key,
        parentType: null
    });

    return _companyTypes;
};

export const getCompanySubTypesOptions = (signupT, companySubTypes) => {
    let companySubTypesOptions = companySubTypes?.map(companySubType => {
        return {
            label: signupT(`company_sub_types.${companySubType}`),
            value: companySubType
        };
    });

    const otherOption = companySubTypesOptions?.find(
        subType => subType.value === 'other'
    );
    companySubTypesOptions = companySubTypesOptions?.filter(
        subType => subType.value !== 'other'
    );
    companySubTypesOptions = arraySorter(companySubTypesOptions, 'label', true);
    if (otherOption) {
        companySubTypesOptions.push(otherOption);
    }

    return companySubTypesOptions;
};

const translateCompanyTypes = companyTypesRaw => {
    const dictionary = {
        'COMPANY_SETTING.LABELS._TYPE_PLANNING_': 'Planning',
        'COMPANY_SETTING.LABELS._TYPE_EVENT_PLANNING_': 'Event Planning',
        'COMPANY_SETTING.LABELS._TYPE_BAKERY_': 'Bakery/Dessert',
        'COMPANY_SETTING.LABELS._TYPE_VENUE_': 'Venue',
        'COMPANY_SETTING.LABELS._TYPE_PHOTOGRAPHY_': 'Photography',
        'COMPANY_SETTING.LABELS._TYPE_RENTALS_': 'Rentals/Photo booths',
        'COMPANY_SETTING.LABELS._TYPE_HAIR_MAKEUP_': 'Hair & Makeup',
        'COMPANY_SETTING.LABELS._TYPE_FLORAL_': 'Floral Design',
        'COMPANY_SETTING.LABELS._TYPE_MUSIC_': 'DJ/Entertainment',
        'COMPANY_SETTING.LABELS._TYPE_FOOD_DRINKS_': 'Food & Drinks',
        'COMPANY_SETTING.LABELS._TYPE_STATIONERY_': 'Stationery',
        'COMPANY_SETTING.LABELS._TYPE_ATTIRE_': 'Fashion/Accessories',
        'COMPANY_SETTING.LABELS._TYPE_CINEMATOGRAPHY_': 'Videography',
        'COMPANY_SETTING.LABELS._TYPE_PROJECT_LIGHTING_': 'Lighting/AV',
        'COMPANY_SETTING.LABELS._TYPE_OFFICIANT_': 'Officiant',
        'COMPANY_SETTING.LABELS._TYPE_PHOTOBOOTH_': 'Photo Booth',
        'COMPANY_SETTING.LABELS._TYPE_OTHER_': 'Other',
        'COMPANY_SETTING.LABELS._TYPE_MARKETING_': 'Marketing/PR',
        'COMPANY_SETTING.LABELS._TYPE_VISUAL_ARTIST_': 'Visual Art',
        'COMPANY_SETTING.LABELS._TYPE_BLOGGER_': 'Blogging',
        'COMPANY_SETTING.LABELS._TYPE_BUSINESS_CONSULTANT_':
            'Business Consulting',
        'COMPANY_SETTING.LABELS._TYPE_AI_CONSULTANT_': 'AI Consulting',
        'COMPANY_SETTING.LABELS._TYPE_CONSULTANT_': 'Consulting',
        'COMPANY_SETTING.LABELS._TYPE_CALLIGRAPHY_': 'Calligraphy',
        'COMPANY_SETTING.LABELS._TYPE_INTERIOR_DESIGNER_': 'Interior Design',
        'COMPANY_SETTING.LABELS._TYPE_GRAPHIC_DESIGNER_': 'Graphic Design',
        'COMPANY_SETTING.LABELS._TYPE_MAKER_ARTISAN_': 'Maker/Artisan',
        'COMPANY_SETTING.LABELS._TYPE_WEB_DESIGNER_': 'Web Design',
        'COMPANY_SETTING.LABELS._TYPE_HEALTH_WELLNESS_': 'Health & Wellness',
        'COMPANY_SETTING.LABELS._TYPE_BOOKKEEPING_ACCOUNTING_':
            'Bookkeeping/Accounting',
        'COMPANY_SETTING.LABELS._TYPE_COACHING_': 'Coaching',
        'COMPANY_SETTING.LABELS._TYPE_CATERING_': 'Catering',
        'COMPANY_SETTING.LABELS._TYPE_AGENCY_FRANCHISE_': 'Agency/Franchise',
        'COMPANY_SETTING.LABELS._TYPE_DOULA_': 'Doula',
        'COMPANY_SETTING.LABELS._TYPE_LAWN_LANDSCAPING_': 'Lawn/Landscaping',
        'COMPANY_SETTING.LABELS._TYPE_EDUCATION_TUTORING_':
            'Education/Tutoring',
        'COMPANY_SETTING.LABELS._TYPE_CLEANING_MAINTENANCE_':
            'Cleaning/Maintenance',
        'COMPANY_SETTING.LABELS._TYPE_FREELANCE_': 'Freelance',
        'COMPANY_SETTING.LABELS._TYPE_CONTRACTOR_':
            'Contractor/Home Improvement',
        'COMPANY_SETTING.LABELS._TYPE_VIRTUAL_ASSISTANT_': 'Virtual Assistant',
        'COMPANY_SETTING.LABELS._TYPE_HOME_STAGING_': 'Home Staging',
        'COMPANY_SETTING.LABELS._TYPE_REAL_ESTATE_': 'Real Estate',
        'COMPANY_SETTING.LABELS._TYPE_SOCIAL_MEDIA_': 'Social Media',
        'COMPANY_SETTING.LABELS._TYPE_MARKETING_CONSULTING_':
            'Marketing Consulting',
        'COMPANY_SETTING.LABELS._TYPE_MARKETING_AGENCY_': 'Marketing Agency',
        'COMPANY_SETTING.LABELS._TYPE_DIGITAL_MARKETING_': 'Digital Marketing',
        'COMPANY_SETTING.LABELS._TYPE_CONTENT_CREATION_': 'Content Creation',
        'COMPANY_SETTING.LABELS._TYPE_COPYWRITING_': 'Copywriting',
        'COMPANY_SETTING.LABELS._TYPE_PUBLIC_RELATIONS_': 'Public Relations',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_PLANNING_': 'planning',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_BAKERY_': 'bakery',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_VENUE_': 'venue',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_PHOTOGRAPHY_': 'photography',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_RENTALS_': 'rental',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_HAIR_MAKEUP_': 'hair and makeup',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_FLORAL_': 'florist',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_MUSIC_': 'entertainment',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_FOOD_DRINKS_': 'food and drink',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_STATIONERY_': 'stationery',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_ATTIRE_': 'fashion',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_CINEMATOGRAPHY_': 'videography',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_PROJECT_LIGHTING_':
            'lighting and AV',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_OFFICIANT_': 'officiant',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_PHOTOBOOTH_': 'photo booth',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_OTHER_': 'other',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_MARKETING_': 'marketing',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_VISUAL_ARTIST_': 'visual art',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_BLOGGER_': 'blogging',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_BUSINESS_CONSULTANT_':
            'consulting',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_AI_CONSULTANT_': 'ai consulting',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_CALLIGRAPHY_': 'calligraphy',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_INTERIOR_DESIGNER_':
            'interior design',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_GRAPHIC_DESIGNER_':
            'graphic design',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_MAKER_ARTISAN_': 'artisan',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_WEB_DESIGNER_': 'web design',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_BOOKKEEPING_ACCOUNTING_':
            'accounting',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_CATERING_': 'catering',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_COACHING_': 'coaching',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_HEALTH_WELLNESS_': 'wellness',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_AGENCY_FRANCHISE_': 'agency',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_DOULA_': 'doula',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_CONTRACTOR_': 'contractor',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_FREELANCER_': 'freelance',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_VIRTUAL_ASSISTANT_':
            'virtual assistant',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_HOME_STAGING_': 'home staging',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_REAL_ESTATE_': 'real estate',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_LAWN_LANDSCAPING_': 'landscape',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_CLEANING_MAINTENANCE_':
            'cleaning',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_SOCIAL_MEDIA_': 'social media',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_EDUCATION_TUTORING_':
            'education',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_MARKETING_CONSULTING_':
            'marketing consulting',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_MARKETING_AGENCY_':
            'marketing agency',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_DIGITAL_MARKETING_':
            'digital marketing',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_CONTENT_CREATION_':
            'content creation',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_COPYWRITING_': 'Copywriting',
        'COMPANY_SETTING.LABELS._TYPE_INFORMAL_PUBLIC_RELATIONS_':
            'public relations'
    };

    return companyTypesRaw.map(companyType => {
        if (!dictionary[companyType.label]) {
            trackError({
                error: new Error(),
                message: `@company.utils:translateCompanyTypes: did not find companyType.label of ${companyType.label} in dictionary definition`,
                context: { companyType }
            });
        }
        return {
            label: dictionary[companyType.label],
            parentType: companyType.parentType,
            value: companyType.value
        };
    });
};

export enum CompanyTypeKeyEnum {
    AiConsultant = 'ai_consultant',
    EventPlanner = 'event_planner',
    Cake = 'cake',
    Venue = 'venue',
    Photographer = 'photographer',
    Cinematography = 'cinematography',
    EventLighting = 'event_lighting',
    Rental = 'rental',
    HairMakeup = 'hair_makeup',
    Florist = 'florist',
    DjMusician = 'dj_musician',
    Caterer = 'caterer',
    Stationery = 'stationery',
    Fashion = 'fashion',
    Officiant = 'officiant',
    PhotoBooth = 'photo_booth',
    Other = 'other',
    Marketing = 'marketing',
    MarketingConsulting = 'marketing_consulting',
    MarketingAgency = 'marketing_agency',
    DigitalMarketing = 'digital_marketing',
    ContentCreation = 'content_creation',
    Copywriting = 'copywriting',
    PublicRelations = 'public_relations',
    VisualArtist = 'visual_artist',
    Blogger = 'blogger',
    BusinessConsultant = 'business_consultant',
    Calligrapher = 'calligrapher',
    InteriorDesigner = 'interior_designer',
    GraphicDesigner = 'graphic_designer',
    MakerArtisan = 'maker_artisan',
    WebDesigner = 'web_designer',
    Accounting = 'accounting',
    Catering = 'catering',
    Coaching = 'coaching',
    Wellness = 'wellness',
    Agency = 'agency',
    Doula = 'doula',
    Landscaper = 'landscape',
    Cleaning = 'cleaning',
    Freelancer = 'freelance',
    SocialMedia = 'social_media',
    RealEstate = 'real_estate',
    Education = 'education',
    VirtualAssistant = 'virtual_assistant',
    HomeStaging = 'home_staging',
    Contractor = 'contractor'
}

export type TCompanyTypeKeyEnum = `${CompanyTypeKeyEnum}`;

export const PartialCompanyProperties = [
    'owner',
    'description',
    'phone_number',
    'email',
    'city',
    'street_address',
    'zip_code',
    'website_url',
    'logo_image',
    'blog_url',
    'cover_images',
    'brand_color',
    'team_members',
    'vendor_emails',
    'default_timezone',
    'brochure_containers',
    'brochure_templates',
    'block_templates',
    'company_url_prefixes',
    'company_url_domain',
    'lead_sources',
    'questionnaires',
    'project_types',
    'default_project_type_label',
    'custom_project_fields',
    'subscribed',
    'allow_ach_refund',
    'company_flow_theme',
    'folders',
    'bank_account_payment_method_id',
    'is_flow_thumbnail_visible_to_client',
    'user_permissions',
    'archived'
];

export function translateUploadedFileToFileDetailsPayload(
    uploadedFiles: IS3UploadFileResponse[],
    files: TUploadedFile[]
): FileDetails[] {
    return uploadedFiles.map((uploadedFile, index) => {
        const file = files[index];

        return {
            filename: uploadedFile.file_name,
            filesize: file.size,
            filetype: file.type,
            type: 'file',
            url: uploadedFile.data
                ? uploadedFile.data.match(
                      /<Location>([\s\S]*?)<\/Location>/
                  )?.[1] || ''
                : ''
        };
    });
}

export function isAttachmentFile(
    item: IImagePreview | IAttachmentFile
): item is IAttachmentFile {
    return 'name' in item && 'type' in item && 'url' in item;
}

export function isUpdateAssetsResponse(
    response: IUpdateAssetsResponse | IWorkspaceAttachments
): response is IUpdateAssetsResponse {
    return 'cover_images' in response && 'company_assets' in response;
}

const EVENT_PLANNER_AND_VENUE_TYPES = [
    'event_planner',
    'event_planning',
    'planning',
    'venue'
];
export function isEventPlannerOrVenue(
    companyType?: string | TCompanyTypes
): boolean {
    return EVENT_PLANNER_AND_VENUE_TYPES.includes(companyType?.toLowerCase());
}
